/**
 * SopEtaBanner
 * ---------------------------------------------------------------------------
 * Tells the user how long an SOP run should take before they click Generate,
 * and — once a run is in flight — how much time is left. Fetches the estimate
 * from POST /api/analysis/estimate-sop/:projectId, which pulls project docs
 * and derives complexity from content_text server-side (no client parsing
 * required).
 *
 * Usage in SOPGenerationTab.tsx:
 *
 *   <SopEtaBanner projectId={currentProject.id} />                       // idle
 *   <SopEtaBanner projectId={currentProject.id} startedAt={startedMs} /> // running
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Clock, Info } from 'lucide-react';
import api from '../../api';

type StageEstimate = { stage: string; wallSeconds: number };
type DocumentEstimate = {
  id?: string | number;
  name?: string;
  estimatedSteps: number;
  stages: StageEstimate[];
  wallSeconds: number;
  wallSecondsRange: [number, number];
};
export type BatchEstimate = {
  mode: 'pre-optimization' | 'post-optimization';
  perDocument: DocumentEstimate[];
  totalWallSeconds: number;
  totalWallSecondsRange: [number, number];
  summary: string;
  assumptions: string[];
};

interface SopEtaBannerProps {
  projectId: number | string;
  /** If set, banner switches to running mode. Unix ms. */
  startedAt?: number;
  /** Optional real progress 0–1 from the job (overrides elapsed-based estimate). */
  progress?: number;
  /** Optional class override so callers can control spacing. */
  className?: string;
}

function humanizeSeconds(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '—';
  if (seconds < 60) return `${Math.round(seconds)} sec`;
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem ? `${h}h ${rem}m` : `${h}h`;
}

export const SopEtaBanner: React.FC<SopEtaBannerProps> = ({
  projectId,
  startedAt,
  progress,
  className = '',
}) => {
  const [estimate, setEstimate] = useState<BatchEstimate | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [expanded, setExpanded] = useState<boolean>(false);
  const [nowMs, setNowMs] = useState<number>(() => Date.now());

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .post<BatchEstimate>(`/api/analysis/estimate-sop/${projectId}`, {})
      .then((res) => {
        if (cancelled) return;
        setEstimate(res.data);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.message || e?.message || 'Estimate unavailable');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  useEffect(() => {
    if (!startedAt) return;
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [startedAt]);

  const isRunning = typeof startedAt === 'number';
  const elapsedSec = isRunning ? Math.max(0, (nowMs - startedAt) / 1000) : 0;

  const effectiveProgress = useMemo(() => {
    if (typeof progress === 'number') return Math.min(0.99, Math.max(0, progress));
    if (!isRunning || !estimate) return 0;
    return Math.min(0.99, elapsedSec / Math.max(1, estimate.totalWallSeconds));
  }, [progress, isRunning, elapsedSec, estimate]);

  if (loading && !estimate) {
    return (
      <div className={`rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-500 flex items-center gap-2 ${className}`} role="status">
        <Clock className="w-4 h-4 animate-pulse" />
        Estimating run time…
      </div>
    );
  }

  if (error || !estimate) {
    // Fall silent rather than blocking the flow — the "Generate" button still works.
    return null;
  }

  const [low, high] = estimate.totalWallSecondsRange;
  const remainingSec = isRunning
    ? Math.max(0, estimate.totalWallSeconds - elapsedSec)
    : estimate.totalWallSeconds;
  const nDocs = estimate.perDocument.length;
  const modeLabel = estimate.mode === 'post-optimization' ? 'optimized' : 'current';

  return (
    <div
      className={`rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-gray-800 ${className}`}
      role="status"
      aria-live="polite"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs font-medium uppercase tracking-wide text-blue-800 flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" />
            {isRunning ? 'Time remaining' : 'Estimated time'}
          </div>
          <div className="mt-1 text-lg font-semibold text-gray-900">
            {isRunning ? `~${humanizeSeconds(remainingSec)} left` : `~${humanizeSeconds(estimate.totalWallSeconds)}`}
            <span className="ml-2 text-sm font-normal text-gray-500">
              ({humanizeSeconds(low)}–{humanizeSeconds(high)})
            </span>
          </div>
          <div className="mt-0.5 text-xs text-gray-600">
            Based on {nDocs} document{nDocs === 1 ? '' : 's'} · {modeLabel} pipeline
          </div>
        </div>

        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="shrink-0 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100"
          aria-expanded={expanded}
        >
          {expanded ? 'Hide breakdown' : 'Show breakdown'}
        </button>
      </div>

      {isRunning && (
        <div className="mt-3">
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-blue-100">
            <div
              className="h-full bg-blue-600 transition-all duration-500"
              style={{ width: `${Math.round(effectiveProgress * 100)}%` }}
            />
          </div>
          <div className="mt-1 flex justify-between text-[11px] text-gray-500">
            <span>Elapsed {humanizeSeconds(elapsedSec)}</span>
            <span>{Math.round(effectiveProgress * 100)}%</span>
          </div>
        </div>
      )}

      {expanded && (
        <div className="mt-4 border-t border-blue-200 pt-3">
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
            Per-document estimate
          </div>
          <ul className="space-y-1.5 max-h-40 overflow-auto">
            {estimate.perDocument.map((d, i) => (
              <li key={String(d.id ?? i)} className="flex items-baseline justify-between gap-3 text-xs">
                <span className="truncate font-medium text-gray-800" title={d.name || undefined}>
                  {d.name || `Document ${i + 1}`}
                </span>
                <span className="whitespace-nowrap text-gray-500">
                  {d.estimatedSteps} steps · {humanizeSeconds(d.wallSeconds)}
                </span>
              </li>
            ))}
          </ul>

          <div className="mt-3 rounded bg-white p-2 text-[11px] leading-relaxed text-gray-500 flex gap-2">
            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-gray-400" />
            <div>
              {estimate.assumptions.map((a, i) => (
                <div key={i}>{a}</div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SopEtaBanner;
